
import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogClose
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CartItem } from "@/contexts/CartContext";
import { useToast } from "@/hooks/use-toast";

interface EditItemModalProps {
  item: CartItem;
  isOpen: boolean;
  onClose: () => void;
  onSave: (id: string, updatedItem: Partial<CartItem>) => void;
}

const EditItemModal: React.FC<EditItemModalProps> = ({ 
  item, 
  isOpen, 
  onClose, 
  onSave 
}) => {
  const { toast } = useToast();
  const [editedItem, setEditedItem] = useState<Partial<CartItem>>({});
  const [startDate, setStartDate] = useState<Date | undefined>(item.startDate);
  const [endDate, setEndDate] = useState<Date | undefined>(item.endDate);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form data when item changes or modal opens
  useEffect(() => {
    if (isOpen) {
      setEditedItem({});
      setStartDate(item.startDate);
      setEndDate(item.endDate);
    }
  }, [item, isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEditedItem(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (checked: boolean, name: string) => {
    setEditedItem(prev => ({ ...prev, [name]: checked }));
  };

  const handleSelectChange = (value: string, name: string) => {
    setEditedItem(prev => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (start: Date | undefined, end: Date | undefined) => {
    if (start) setStartDate(start);
    if (end) setEndDate(end);
    
    if (start && end) {
      setEditedItem(prev => ({
        ...prev,
        startDate: start,
        endDate: end
      }));
      console.log("Dates updated:", { start, end });
    }
  };

  const validateForm = (): boolean => {
    // Basic validation
    if (editedItem.customerEmail && !/^\S+@\S+\.\S+$/.test(editedItem.customerEmail as string)) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address",
        variant: "destructive"
      });
      return false;
    }

    if (startDate && endDate && startDate > endDate) {
      toast({
        title: "Invalid dates",
        description: "End date must be after start date",
        variant: "destructive"
      });
      return false;
    }

    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    // Ensure dates are included in the edited item
    const updatedItem = {
      ...editedItem,
      startDate,
      endDate
    };
    
    try {
      console.log("Saving updated item:", updatedItem);
      onSave(item.id, updatedItem);
      onClose();
      toast({
        title: "Success",
        description: `${item.permitName} has been updated`
      });
    } catch (error) {
      console.error("Error updating item:", error);
      toast({
        title: "Error",
        description: "Failed to update item. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Determine which fields to show based on the item type
  const showFishingFields = item.type === "fishing";
  const showCampingFields = item.type === "camping" || item.type === "rv-booking" || item.type === "general-camping" || (item.type === "fishing" && item.includesCamping);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md md:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit {item.permitName}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-4">
            {/* Customer Information Section */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium">Customer Information</h3>
              
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <Label htmlFor="customerName">Name</Label>
                  <Input 
                    id="customerName"
                    name="customerName"
                    defaultValue={item.customerName}
                    onChange={handleInputChange}
                  />
                </div>
                
                <div>
                  <Label htmlFor="customerEmail">Email</Label>
                  <Input 
                    id="customerEmail"
                    name="customerEmail"
                    type="email"
                    defaultValue={item.customerEmail}
                    onChange={handleInputChange}
                  />
                </div>
                
                <div>
                  <Label htmlFor="phoneNumber">Phone Number</Label>
                  <Input 
                    id="phoneNumber"
                    name="phoneNumber"
                    defaultValue={item.phoneNumber || ""}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
            
            {/* Address Information Section */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium">Address</h3>
              
              <div>
                <Label htmlFor="address">Street Address</Label>
                <Input 
                  id="address"
                  name="address"
                  defaultValue={item.address || ""}
                  onChange={handleInputChange}
                />
              </div>
              
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input 
                    id="city"
                    name="city"
                    defaultValue={item.city || ""}
                    onChange={handleInputChange}
                  />
                </div>
                
                <div>
                  <Label htmlFor="state">State</Label>
                  <Input 
                    id="state"
                    name="state"
                    defaultValue={item.state || ""}
                    onChange={handleInputChange}
                  />
                </div>
                
                <div>
                  <Label htmlFor="zipCode">Zip Code</Label>
                  <Input 
                    id="zipCode"
                    name="zipCode"
                    defaultValue={item.zipCode || ""}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
            
            {/* Dates Section */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium">
                {showFishingFields ? "Fishing Dates" : "Camping Dates"}
              </h3>
              
              <DateRangePicker
                startDate={startDate}
                endDate={endDate}
                setStartDate={(date) => handleDateChange(date, endDate)}
                setEndDate={(date) => handleDateChange(startDate, date)}
                startLabel={showFishingFields ? "Start Date" : "Check-in Date"}
                endLabel={showFishingFields ? "End Date" : "Check-out Date"}
                displayUnit={showFishingFields ? "days" : "nights"}
              />
            </div>
            
            {/* Fishing Specific Fields */}
            {showFishingFields && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium">Fishing Options</h3>
                
                <div className="flex items-center space-x-2 pt-2">
                  <Checkbox 
                    id="includesCamping"
                    checked={editedItem.includesCamping !== undefined 
                      ? editedItem.includesCamping 
                      : item.includesCamping}
                    onCheckedChange={(checked) => 
                      handleCheckboxChange(Boolean(checked), "includesCamping")}
                  />
                  <Label htmlFor="includesCamping">
                    Include camping add-on ($15 per night)
                  </Label>
                </div>
              </div>
            )}
            
            {/* Camping Specific Fields */}
            {(item.type === "general-camping") && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium">Stay Type</h3>
                
                <Select 
                  defaultValue={item.stayType || "nightly"} 
                  onValueChange={(value) => handleSelectChange(value, "stayType")}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select stay type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nightly">Nightly ($15/night)</SelectItem>
                    <SelectItem value="weekly">Weekly ($75/week)</SelectItem>
                    <SelectItem value="monthly">Monthly ($150/month)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Vehicle Information if applicable */}
            {showCampingFields && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium">Vehicle Information</h3>
                
                <div>
                  <Label htmlFor="licensePlate">License Plate</Label>
                  <Input 
                    id="licensePlate"
                    name="licensePlate"
                    defaultValue={item.licensePlate || ""}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            )}
            
            {/* Additional Fields */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium">Additional Information</h3>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="bringingPets"
                  checked={editedItem.bringingPets !== undefined 
                    ? editedItem.bringingPets 
                    : item.bringingPets || false}
                  onCheckedChange={(checked) => 
                    handleCheckboxChange(Boolean(checked), "bringingPets")}
                />
                <Label htmlFor="bringingPets">Bringing Pets</Label>
              </div>
              
              <div>
                <Label htmlFor="additionalDetails">Additional Details</Label>
                <Textarea 
                  id="additionalDetails"
                  name="additionalDetails"
                  defaultValue={item.additionalDetails || ""}
                  onChange={handleInputChange}
                  className="h-24"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter className="pt-4">
            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="bg-tribal-primary hover:bg-tribal-primary/90"
            >
              {isSubmitting ? "Saving..." : "Save Changes"}
            </Button>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditItemModal;
